/* Reuse the actual Dawn menu nodes so destinations, IDs and disclosure behavior stay intact. */
if (!customElements.get('ctfa-overflow-menu')) {
  customElements.define('ctfa-overflow-menu', class extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-ctfa-primary]');
      this.more = this.querySelector('[data-ctfa-more]');
      this.overflow = this.querySelector('[data-ctfa-overflow]');
      this.header = this.closest('header');
      if (!this.list || !this.more || !this.overflow || !this.header) return;
      this.items = [...Array.from(this.list.children).filter(item => item !== this.more), ...this.overflow.children];
      this.details = this.more.querySelector('details');
      this.trigger = this.details.querySelector('summary');
      this.onResize = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.layout());
      };
      this.onEscape = event => {
        if (event.key !== 'Escape') return;
        const opened = event.target.closest('details[open]');
        if (!opened || !this.contains(opened)) return;
        event.preventDefault();
        event.stopPropagation();
        opened.open = false;
        const control = opened.querySelector('summary');
        control.setAttribute('aria-expanded', 'false');
        control.focus();
      };
      this.onToggle = event => {
        const detail = event.target;
        if (!(detail instanceof HTMLDetailsElement)) return;
        detail.querySelector('summary')?.setAttribute('aria-expanded', String(detail.open));
        if (detail === this.details && !detail.open) this.closeChildren();
        if (detail.open) {
          // A normal top-level menu and More cannot overlap.
          for (const other of this.list.querySelectorAll(':scope > li > header-menu > details, :scope > li > details')) {
            if (other !== detail && !other.contains(detail)) other.open = false;
          }
        }
      };
      this.onOutside = event => {
        if (!this.contains(event.target)) this.closeMore();
      };
      this.onFocusOut = () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.closeMore();
        }, 0);
      };
      this.addEventListener('keyup', this.onEscape, true);
      this.addEventListener('toggle', this.onToggle, true);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('pointerdown', this.onOutside);
      window.addEventListener('resize', this.onResize);
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.header);
      }
      this.setAttribute('data-ready', '');
      this.onResize();
      document.fonts?.ready.then(() => { if (this.isConnected) this.onResize(); });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.focusTimer);
      this.observer?.disconnect();
      this.removeEventListener('keyup', this.onEscape, true);
      this.removeEventListener('toggle', this.onToggle, true);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('pointerdown', this.onOutside);
      window.removeEventListener('resize', this.onResize);
    }
    closeChildren() {
      this.overflow.querySelectorAll('details[open]').forEach(detail => {
        detail.open = false;
        detail.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });
    }
    closeMore() {
      this.details.open = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.closeChildren();
    }
    layout() {
      if (!this.isConnected) return;
      const focused = document.activeElement;
      const focusInside = this.contains(focused);
      const focusedItem = this.items.find(item => item.contains(focused));
      this.closeMore();
      this.items.forEach(item => this.list.insertBefore(item, this.more));
      this.more.hidden = true;
      this.header.classList.remove('ctfa-header--drawer');
      if (window.innerWidth < 990) {
        if (focusInside) this.header.querySelector('header-drawer summary')?.focus();
        return;
      }
      const available = this.getBoundingClientRect().width;
      if (!available) return;
      const widths = this.items.map(item => item.getBoundingClientRect().width);
      let used = widths.reduce((sum, width) => sum + width, 0);
      if (used > available + 1) {
        this.more.hidden = false;
        used += this.more.getBoundingClientRect().width;
        let keep = this.items.length;
        const minimum = Math.min(2, this.items.length);
        while (used > available + 1 && keep > minimum) {
          keep--;
          used -= widths[keep];
          this.overflow.insertBefore(this.items[keep], this.overflow.firstChild);
        }
        if (used > available + 1) {
          this.items.forEach(item => this.list.insertBefore(item, this.more));
          this.more.hidden = true;
          this.header.classList.add('ctfa-header--drawer');
        }
      }
      const drawer = this.header.querySelector('header-drawer');
      if (!this.header.classList.contains('ctfa-header--drawer') && drawer?.mainDetailsToggle?.open) {
        const summary = drawer.mainDetailsToggle.querySelector('summary');
        drawer.closeMenuDrawer(new Event('resize'), summary);
        summary.setAttribute('aria-expanded', 'false');
        this.items[0]?.querySelector('summary, a')?.focus();
      }
      document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.ceil(this.header.getBoundingClientRect().bottom)}px`);
      if (focusInside) {
        if (this.header.classList.contains('ctfa-header--drawer')) this.header.querySelector('header-drawer summary')?.focus();
        else if (focusedItem && this.overflow.contains(focusedItem)) this.trigger.focus();
        else if (focused === this.trigger && this.more.hidden) this.items[0]?.querySelector('summary, a')?.focus();
        else if (focused?.isConnected) focused.focus();
      }
    }
  });
}
